import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';

interface AudioRegistration {
  playPause: () => void;
}

interface StoryAudioContextValue {
  playPause: () => void;
  isPlaying: boolean;
  hasAudio: boolean;
  hasAudioFinished: boolean;
  /** Transcripción lista (o sin transcript): el play de la barra puede animar con el karaoke. */
  karaokeEntranceReady: boolean;
  /**
   * Velocidad de reproducción elegida por el alumno (1 = normal). La fija la
   * barra inferior (PlaybackSpeedDrawer) y la aplica el KaraokePlayer, que es
   * quien posee el elemento <audio>. Se resetea a 1 con cada registro (cada
   * historia empieza a velocidad normal, igual que las conversaciones).
   */
  playbackSpeed: number;
  setPlaybackSpeed: (speed: number) => void;
  register: (reg: AudioRegistration) => void;
  unregister: () => void;
  syncIsPlaying: (isPlaying: boolean) => void;
  syncAudioFinished: (finished: boolean) => void;
  syncKaraokeEntranceReady: (ready: boolean) => void;
  markAudioFinished: () => void;
}

const StoryAudioContext = createContext<StoryAudioContextValue | null>(null);

export function StoryAudioProvider({ children }: { children: ReactNode }) {
  const registrationRef = useRef<AudioRegistration | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasAudio, setHasAudio] = useState(false);
  const [hasAudioFinished, setHasAudioFinished] = useState(false);
  const [karaokeEntranceReady, setKaraokeEntranceReady] = useState(false);
  const [playbackSpeed, setPlaybackSpeedState] = useState(1);

  const setPlaybackSpeed = useCallback((speed: number) => {
    setPlaybackSpeedState(speed);
  }, []);

  const syncIsPlaying = useCallback((v: boolean) => {
    setIsPlaying(v);
  }, []);

  const syncAudioFinished = useCallback((v: boolean) => {
    setHasAudioFinished(v);
  }, []);

  const syncKaraokeEntranceReady = useCallback((ready: boolean) => {
    setKaraokeEntranceReady(ready);
  }, []);

  const register = useCallback((reg: AudioRegistration) => {
    registrationRef.current = reg;
    setHasAudio(true);
    setIsPlaying(false);
    setHasAudioFinished(false);
    setKaraokeEntranceReady(false);
    setPlaybackSpeedState(1);
  }, []);

  const unregister = useCallback(() => {
    registrationRef.current = null;
    setHasAudio(false);
    setIsPlaying(false);
    setHasAudioFinished(false);
    setKaraokeEntranceReady(false);
  }, []);

  const playPause = useCallback(() => {
    registrationRef.current?.playPause();
  }, []);

  const markAudioFinished = useCallback(() => {
    setHasAudioFinished(true);
  }, []);

  const value: StoryAudioContextValue = {
    playPause,
    isPlaying,
    hasAudio,
    hasAudioFinished,
    karaokeEntranceReady,
    playbackSpeed,
    setPlaybackSpeed,
    register,
    unregister,
    syncIsPlaying,
    syncAudioFinished,
    syncKaraokeEntranceReady,
    markAudioFinished,
  };

  return (
    <StoryAudioContext.Provider value={value}>
      {children}
    </StoryAudioContext.Provider>
  );
}

export function useStoryAudio() {
  return useContext(StoryAudioContext);
}
